import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Actions from "../../components/Common/Actions";
import AntdBreadcrumbs from "../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../components/Common/Buttons/FilledButton";
import DoctorsFilterOptions from "../../components/Common/CommonFilterOptions";
import { convertToTitleCase } from "../../components/Constants";
import ListPageLayout from "../../components/Layout";
import Deletepopup from "../../components/Modal/Deletepopup";
import useDebouncedSearch from "../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../components/Utils/hooks/useSortHandler";

const FrpCities = () => {
  const { stateSlug } = useParams();
  const navigate = useNavigate();
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  const addNewCityButton = {
    id: "addNewCity",
    onClick: () => navigate(`/frp-states/add-city/${stateSlug}`),
    style: { width: "240px" },
    label: "Add New City",
  };

  const {
    records: cities,
    totalCount,
    pagination,
    setPagination,
    fetchData,
  } = usePaginatedData({
    apiUrl: `${process.env.REACT_APP_API_URL}frp-states/city-list/${stateSlug}`,
    method: "get",
    params: { searchText: search, sort },
  });

  const handlePageChange = usePageChange(pagination, setPagination);

  const handleDelete = useDeleteHandler(
    `frp-states/${stateSlug}`,
    fetchData,
    setShowDelete,
  );

  const updateActiveStatus = (city) => {
    axios
      .post(
        `${process.env.REACT_APP_API_URL}frp-states/${stateSlug}/edit-city/${city.slug_url}`,
        {
          ...city,
          active: !city.active,
        },
      )
      .then(() => fetchData())
      .catch(console.error);
  };

  useEffect(() => {
    if (stateSlug) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search, stateSlug]);

  const columns = [
    {
      title: "S.No",
      key: "serial",
      width: 80,
      render: (_, __, idx) => Number(pagination.skip || 0) + (idx + 1),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: 200,
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      width: 150,
      render: (active) => (active ? "Active" : "Inactive"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Actions
          isEdit
          isDelete
          isStatus
          status={record.active}
          onClickEdit={() =>
            navigate(`/frp-states/${stateSlug}/edit-city/${record.slug_url}`)
          }
          onClickDelete={() => {
            setDeleteId(record.slug_url);
            setShowDelete(true);
          }}
          onClickStatus={() => updateActiveStatus(record)}
        />
      ),
    },
  ];

  const dataSource = cities.map((c) => ({ ...c, key: c._id }));

  return (
    <ListPageLayout
      breadcrumbs={
        <AntdBreadcrumbs
          breadcrumbsList={[
            { text: "FRP / States", link: "/frp-states" },
            { text: `Cities in ${convertToTitleCase(stateSlug)}`, link: "" },
          ]}
        />
      }
      addButton={<FilledButton props={addNewCityButton} />}
      titles={null}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((p) => ({
              ...p,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => {}}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource,
        className: "mx-3 pt-2 table-scrollable medicineList_table",
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={({ selected }) => handlePageChange({ selected })}
    >
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}
    </ListPageLayout>
  );
};

export default FrpCities;
